import { useEffect, useRef, useCallback, useState } from 'react';
import {
  SRA_TIMEOUT_OPTIONS,
  SRA_TIMEOUT_SETTING,
  normalizeSraTimeout,
} from '@admin-console/shared/services/account/SraTimeout';

export const AUTO_SIGNOUT_KEY = SRA_TIMEOUT_SETTING;

/** Total session timeout in ms (including grace period) per setting value; null = never. */
const TIMEOUT_MS: Record<string, number | null> = Object.fromEntries(
  SRA_TIMEOUT_OPTIONS.map((option) => [option.value, option.minutes === null ? null : option.minutes * 60 * 1000])
);

/** Warning countdown displayed before automatic sign-out (seconds). */
export const GRACE_SECONDS = 120;
const GRACE_MS = GRACE_SECONDS * 1000;

/** Returns the idle-before-warning delay in ms, or null if disabled. */
function getIdleMs(): number | null {
  try {
    // Unset/unknown falls back to the default (1 hour); legacy 'disabled' maps to 'never'.
    const total = TIMEOUT_MS[normalizeSraTimeout(localStorage.getItem(AUTO_SIGNOUT_KEY))];
    if (!total) return null;
    return Math.max(total - GRACE_MS, 10_000); // at least 10s gap before warning
  } catch {
    return null;
  }
}

export interface AutoSignOutHookResult {
  showWarning: boolean;
  countdown: number;   // seconds remaining in grace period
  dismiss: () => void; // user clicked "Stay Signed In"
  signOutNow: () => void;
}

/**
 * Tracks user activity and triggers an auto sign-out after the configured
 * idle period.  A 2-minute warning modal is shown before sign-out fires.
 *
 * @param onSignOut – called when the session should be terminated.
 * @param onStay – called when the user keeps the session alive from the warning (button or dismissal).
 */
export function useAutoSignOut(onSignOut: () => void, onStay?: () => void): AutoSignOutHookResult {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(GRACE_SECONDS);

  // Refs — avoids stale-closure issues in event callbacks
  const inGraceRef      = useRef(false);
  const idleRef         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSignOutRef    = useRef(onSignOut);
  onSignOutRef.current  = onSignOut; // always current without triggering effects
  const onStayRef       = useRef(onStay);
  onStayRef.current     = onStay;

  // ── Helpers ──────────────────────────────────────────────────────────────

  const stopAll = useCallback(() => {
    if (idleRef.current)     { clearTimeout(idleRef.current);     idleRef.current = null; }
    if (graceRef.current)    { clearTimeout(graceRef.current);    graceRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    inGraceRef.current = false;
  }, []);

  const executeSignOut = useCallback(() => {
    stopAll();
    setShowWarning(false);
    onSignOutRef.current();
  }, [stopAll]);

  const startGrace = useCallback(() => {
    inGraceRef.current = true;
    let secs = GRACE_SECONDS;
    setCountdown(secs);
    setShowWarning(true);

    // Tick every second to update the displayed countdown
    intervalRef.current = setInterval(() => {
      secs -= 1;
      setCountdown(secs);
      if (secs <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    // Actual sign-out fires at the end of the grace period
    graceRef.current = setTimeout(executeSignOut, GRACE_MS);
  }, [executeSignOut]);

  const arm = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    const ms = getIdleMs();
    if (ms === null) return; // timeout disabled or not set
    idleRef.current = setTimeout(startGrace, ms);
  }, [startGrace]);

  // ── Public API ────────────────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    stopAll();
    setShowWarning(false);
    setCountdown(GRACE_SECONDS);
    arm();
    onStayRef.current?.();
  }, [stopAll, arm]);

  // ── Activity detection ────────────────────────────────────────────────────

  useEffect(() => {
    // Listeners stay attached even when the timeout is "never": arm() is a no-op then, and a later
    // setting change (autoSignOutSettingChanged) starts working without remounting.
    const onActivity = () => {
      if (inGraceRef.current) return; // don't reset during grace period
      arm();
    };

    const events = [
      'mousemove', 'mousedown', 'keydown',
      'touchstart', 'scroll', 'click',
    ] as const;

    events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
    arm(); // start the first idle timer

    return () => {
      events.forEach(e => document.removeEventListener(e, onActivity));
      stopAll();
    };
  }, [arm, stopAll]);

  // ── React to setting changes ───────────────────────────────────────────────

  const rearm = useCallback(() => {
    stopAll();
    setShowWarning(false);
    setCountdown(GRACE_SECONDS);
    arm();
  }, [stopAll, arm]);

  useEffect(() => {
    // Cross-tab changes via the Web Storage API
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUTO_SIGNOUT_KEY) rearm();
    };
    // Same-tab changes dispatched by SettingsView after saving
    const onCustom = () => rearm();

    window.addEventListener('storage', onStorage);
    window.addEventListener('autoSignOutSettingChanged', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('autoSignOutSettingChanged', onCustom);
    };
  }, [rearm]);

  return { showWarning, countdown, dismiss, signOutNow: executeSignOut };
}
